import { MEASURED_SIGNALS_EXPLANATION } from '../acceptance/safetyScanPresentationCopy';

/**
 * Epic 160: external-only language sanitization (reviewer page + external exports).
 * Do not use in warehouse/admin surfaces.
 */

export const OBSERVED_SIGNALS_EXPLANATION = MEASURED_SIGNALS_EXPLANATION;

export const EXTERNAL_DEMO_SUCCESS_METRIC =
  'Production success metric: reduction in exposure rate per monitored observation hour, measured only on live site data with matched baseline and follow-up windows.';

export const EXTERNAL_CRITICAL_EXPOSURE_DEMO =
  'Pedestrian-mobile-equipment interaction in a restricted forklift zone creates serious injury potential.';

export const EXTERNAL_RECOMMENDED_ACTION_DEMO =
  'Review pedestrian separation controls and schedule follow-up measurement.';

export const REPRESENTATIVE_EVIDENCE_NOTE =
  'Evidence samples are included to support human review. They are not used for worker scoring or disciplinary ranking.';

export const REPRESENTATIVE_EVIDENCE_PACKAGE_FALLBACK =
  'Representative evidence is included in the signed Safety Scan package.';

export const EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP =
  'Corrective action was logged as completed in the workflow. Follow-up effectiveness was not evaluated in this controlled demo.';

export const EXTERNAL_ADMIN_CONTROL_RATIONALE =
  'Administrative controls may support the response, but engineering or layout controls should be considered when pedestrian exposure in vehicle-movement areas persists.';

export const EXTERNAL_PPE_CONTROL_RATIONALE =
  'PPE may support visibility where site policy requires it, but it should not be treated as the primary control for vehicle-pedestrian separation.';

/** Terms that must not appear on external reviewer surfaces (Tests E). */
export const PROHIBITED_EXTERNAL_TERMS = [
  'Risk detected',
  'Claim status',
  'Claim Status Notes',
  'zone violation',
  'zone_violation',
  'event signals',
  'observed event signals',
  'Withheld Pending Validation',
  'Success metric: Production success metric',
  'Follow-up scan showed continued monitoring with Corrective action',
  'AI person detection',
  'Improvement validated',
  'validated outcomes',
  'corrective actions validated',
  'corrective actions were completed and validated',
  'Cloud Config Dashboard',
  'Org: acme',
  'Site: yyz',
  'Pipeline: Degraded',
  'Open Current Workflow',
  'Technical Details',
  'No signed-off analytics were returned for this card',
  'Claim Status Boundary note',
  'Live site feed',
  'worker violation',
  'unsafe worker',
  'repeat offender',
  'acme',
  'yyz',
  'demo5',
  'demo5tes',
  'cam_',
  'lead@site.com',
  'workflow_id',
] as const;

const PHRASE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\bRisk detected\b/gi, 'Workplace condition observed'],
  [/\bClaim status\b/gi, 'Claim boundaries'],
  [/\bClaim Status Boundary note\b/gi, 'Claim boundaries'],
  [/\bClaim status boundary note\b/gi, 'Claim boundaries'],
  [/\bOrg:\s*acme\b/gi, 'Org: Demo warehouse'],
  [/\bSite:\s*yyz\b/gi, 'Site: Demo warehouse'],
  [/\bPipeline:\s*Degraded\b/gi, ''],
  [/\bOpen Current Workflow\b/gi, ''],
  [/\bTechnical Details\b/gi, 'Technical appendix'],
  [/\bNo signed-off analytics were returned for this card\b/gi, ''],
  [/\bzone violations?\b/gi, 'restricted-zone exposure'],
  [/\bzone violation\b/gi, 'restricted-zone exposure'],
  [/\bzone_violation\b/gi, 'restricted-zone exposure'],
  [/\bAI person detection in restricted zone\b/gi, 'pedestrian presence detected in a defined restricted zone'],
  [/\bAI person detection\b/gi, 'pedestrian presence detected'],
  [/\bPedestrian in forklift area\b/gi, 'Pedestrian exposure in forklift movement area'],
  [/\bdemosites\b/gi, 'Demo site area'],
  [/\bobserved event signals\b/gi, 'measured signals'],
  [/\bevent signals\b/gi, 'measured signals'],
  [/\bobserved signals\b/gi, 'measured signals'],
  [/\bWithheld Pending Validation\b/gi, 'Requires live-site measurement'],
  [/\bvalidated outcomes\b/gi, 'follow-up status'],
  [/\bImprovement validated\b/gi, 'Follow-up measurement status recorded'],
  [/\bworker violations?\b/gi, 'workplace condition'],
  [/\bworker violation\b/gi, 'workplace condition'],
  [/\bunsafe worker\b/gi, 'workplace condition'],
  [/\brepeat offender\b/gi, 'recurring exposure pattern'],
];

const DEMO_VALIDATION_SUPPRESSION: Array<[RegExp, string]> = [
  [/\bImprovement validated\b/gi, 'Follow-up measurement status recorded'],
  [/\bCorrective action effectiveness verified\b/gi, 'Corrective action logged as completed'],
  [/\bObserved exposure rate decreased by [\d.]+%/gi, 'Exposure reduction is not claimed from this report'],
  [/\bvalidated outcomes\b/gi, 'follow-up status'],
  [/\bcorrective actions were completed and validated(?: for the linked workflow)?\b/gi, 'Corrective action was logged as completed in the workflow.'],
  [/\bcorrective actions validated\b/gi, 'Corrective action was logged as completed in the workflow.'],
  [
    /\bfollow-up scan showed continued monitoring with corrective actions validated\b/gi,
    EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
  ],
  [
    /\bfollow-up scan showed continued monitoring with corrective action was logged as completed in the workflow\.?\b/gi,
    EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
  ],
  [
    /\bfollow-up scan showed continued monitoring with\b/gi,
    'Follow-up effectiveness was not evaluated in this controlled demo.',
  ],
  [/\bImproved\b/g, 'Corrective action was logged as completed in the workflow.'],
  [/\bNot Yet Validated\b/gi, 'Follow-up measurement not evaluated on controlled demo footage'],
  [/\bWithheld Pending Validation\b/gi, 'Not evaluated on demo source'],
];

const INTERNAL_ID_PATTERNS: RegExp[] = [
  /\bacme\b/gi,
  /\byyz\b/gi,
  /\bdemo5tes\b/gi,
  /\bdemo5\b/gi,
  /\blead@site\.com\b/gi,
  /\bcam_[a-z0-9_]+\b/gi,
  /\bworkflow_id\b/gi,
  /\bCloud Config Dashboard\b/gi,
];

export type SanitizeExternalLabelOptions = {
  isDemo?: boolean;
};

export function redactInternalIds(value: string): string {
  let text = value;
  INTERNAL_ID_PATTERNS.forEach((pattern) => {
    text = text.replace(pattern, (match) => {
      const lowered = match.toLowerCase();
      if (lowered === 'acme' || lowered === 'yyz') return 'Demo warehouse';
      if (lowered.includes('demo5')) return 'Restricted Forklift Zone';
      if (lowered.startsWith('cam_')) return 'Controlled demo feed';
      if (lowered === 'workflow_id') return 'linked workflow';
      if (lowered.includes('lead@site.com')) return 'site contact';
      return '';
    });
  });
  return text.replace(/\s{2,}/g, ' ').trim();
}

export function suppressDemoValidationLanguage(value: string): string {
  let text = value;
  DEMO_VALIDATION_SUPPRESSION.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  if (/decreased by\s+[\d.]+%/i.test(text)) {
    return 'Exposure reduction is not claimed from this report';
  }
  return text;
}

export function collapseExternalCorrectiveActionCopy(value: string): string {
  let text = String(value || '').trim();
  if (!text) return text;
  if (
    /corrective action was logged as completed/i.test(text)
    && /follow-up effectiveness was not evaluated/i.test(text)
  ) {
    return EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP;
  }
  text = text.replace(
    /(Corrective action was logged as completed in the workflow\.\s*)+/gi,
    'Corrective action was logged as completed in the workflow. ',
  );
  text = text.replace(
    /(Follow-up effectiveness was not evaluated in this controlled demo\.\s*)+/gi,
    'Follow-up effectiveness was not evaluated in this controlled demo. ',
  );
  return text.replace(/\s{2,}/g, ' ').trim();
}

export function sanitizeExternalControlRationale(
  level: string | null | undefined,
  fallback: string,
  options: SanitizeExternalLabelOptions = {},
): string {
  const normalized = String(level || '').trim().toLowerCase();
  if (normalized === 'administrative') return EXTERNAL_ADMIN_CONTROL_RATIONALE;
  if (normalized === 'ppe') return EXTERNAL_PPE_CONTROL_RATIONALE;
  return sanitizeExternalLabel(fallback, options);
}

export function formatExternalFormalClaimStatus(
  value?: string | null,
  options: SanitizeExternalLabelOptions = {},
): string {
  const raw = String(value || '').trim();
  if (!raw || /withheld/i.test(raw)) {
    return options.isDemo ? 'Not evaluated on demo source' : 'Requires live-site measurement';
  }
  return sanitizeExternalLabel(raw, options);
}

export function sanitizeExternalLabel(
  value?: string | null,
  options: SanitizeExternalLabelOptions = {},
): string {
  let text = String(value || '').trim();
  if (!text) return '';
  PHRASE_REPLACEMENTS.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  if (options.isDemo) {
    text = suppressDemoValidationLanguage(text);
    text = collapseExternalCorrectiveActionCopy(text);
    text = text.replace(/\bWithheld Pending Validation\b/gi, 'Not evaluated on demo source');
  }
  text = text.replace(/\bSuccess metric:\s*Production success metric:\s*/gi, 'Production success metric: ');
  return redactInternalIds(text);
}

export function containsProhibitedExternalTerm(value: string): string | null {
  const haystack = String(value || '');
  for (const term of PROHIBITED_EXTERNAL_TERMS) {
    if (haystack.toLowerCase().includes(term.toLowerCase())) {
      return term;
    }
  }
  return null;
}

export function resolveExternalClosureResultLabel(
  resultLabel: string | null | undefined,
  options: { isDemo: boolean },
): string | null {
  const raw = String(resultLabel || '').trim();
  if (!raw) return null;
  if (options.isDemo && /improved|validated/i.test(raw)) {
    return 'Corrective action logged as completed';
  }
  return sanitizeExternalLabel(raw, { isDemo: options.isDemo }) || null;
}

export function resolveExternalDemoFollowUpCopy(isDemo: boolean): {
  followUpMeasurement: string;
  exposureReductionClaim: string;
} {
  if (!isDemo) {
    return {
      followUpMeasurement: 'Follow-up measurement uses matched windows on live site data.',
      exposureReductionClaim: 'Exposure reduction claims require trusted observation time.',
    };
  }
  return {
    followUpMeasurement: 'Follow-up measurement not evaluated on controlled demo footage',
    exposureReductionClaim: 'Exposure reduction is not claimed from this report',
  };
}
